import {Injectable, Injector} from "@angular/core";

import {ReportError} from "app/app.service";
import {ApiService} from "app/services/domain/api.service";
import * as SharedSvc from "app/services/domain/base.service";
import {AutoRetryRequest, BaseListService, BatchResponse} from "app/services/domain/base.service";
import {WafAssessmentVersionsService} from "app/services/domain/waf-assessment-versions.service";
import {WorkloadDefinitionExtended} from "app/services/domain/workload-definitions.service";
import * as Models from "app/services/proxy/model/models";

import {CacheService} from "framework/services/cache.service";
import {ErrorService} from "framework/services/error.service";
import {Lookup} from "framework/services/utils.service";
import {MomentHelper} from "framework/ui/formatting/date-format.pipe";
import {Memoizer, ResetMemoizers} from "framework/utils/memoizers";

@Injectable()
export class WafAssessmentDefinitionsService extends SharedSvc.BaseDefinitionService<Models.WellArchitectedFrameworkAssessmentConfiguration, Models.WellArchitectedFrameworkAssessmentDefinition, Models.WellArchitectedFrameworkAssessmentDefinitionVersion, WafAssessmentDefinitionExtended>
{
    constructor(api: ApiService,
                errors: ErrorService,
                cache: CacheService)
    {
        super(api, errors, cache, Models.WellArchitectedFrameworkAssessmentDefinition, WafAssessmentDefinitionExtended.newInstance);
    }

    public createDefinition(): Promise<Models.WellArchitectedFrameworkAssessmentDefinition>
    {
        return this.domain.apis.wafAssessmentDefinitionsApi.create();
    }

    public setState(definition: string,
                    model: Models.WellArchitectedFrameworkAssessmentDefinition): Promise<Models.WellArchitectedFrameworkAssessmentDefinition>
    {
        return this.domain.apis.wafAssessmentDefinitionsApi.setState(definition, model);
    }

    public remove(definition: string): Promise<boolean>
    {
        return this.api.wafAssessmentDefinitionsApi.remove(definition);
    }

    @ReportError
    public async getAll(): Promise<Models.RecordIdentity[]>
    {
        return null;
    }

    public getHistory(sysId: string): Promise<Models.WellArchitectedFrameworkAssessmentDefinitionVersion[]>
    {
        return this.api.wafAssessmentDefinitionsApi.getHistory(sysId);
    }

    //--//

    protected cachePrefix(): string { return "WafAssessmentDefinition"; }

    protected getRaw(id: string): Promise<Models.WellArchitectedFrameworkAssessmentDefinition>
    {
        return AutoRetryRequest(20, () => this.api.wafAssessmentDefinitionsApi.get(id));
    }

    protected getBatchRaw(ids: string[]): Promise<Models.WellArchitectedFrameworkAssessmentDefinition[]>
    {
        const request = Models.RecordIdentityBatchRequest.newInstance({ids: ids.map(id => WafAssessmentDefinitionExtended.newIdentity(id))});
        return this.api.wafAssessmentDefinitionsApi.batch(request);
    }

    protected canBatch(): boolean
    {
        return true;
    }
}

export class WafAssessmentDefinitionExtended extends SharedSvc.ExtendedDefinitionModel<Models.WellArchitectedFrameworkAssessmentConfiguration, Models.WellArchitectedFrameworkAssessmentDefinition, Models.WellArchitectedFrameworkAssessmentDefinitionVersion>
{
    static newInstance(svc: WafAssessmentDefinitionsService,
                       model: Models.WellArchitectedFrameworkAssessmentDefinition): WafAssessmentDefinitionExtended
    {
        return new WafAssessmentDefinitionExtended(svc, model, "WafAssessmentDefinition");
    }

    public static newIdentity(sysId?: string): Models.RecordIdentity
    {
        return Models.RecordIdentity.newInstance({
                                                     table: "WafAssessmentDefinition",
                                                     sysId: sysId
                                                 });
    }

    //--//

    protected definitionSvc(): WafAssessmentDefinitionsService
    {
        return this.domain.wafAssessmentDefinitions;
    }

    protected definitionVersionSvc(): WafAssessmentVersionsService
    {
        return this.domain.wafAssessmentVersions;
    }

    public async compatibleWorkloadFrameworks(): Promise<boolean>
    {
        const fetcher      = this.domain.wafFrameworks.getFrameworksFetcher(this.domain.users.activeTenantId);
        const [releaseExt] = await Promise.all([
                                                   this.getRelease(),
                                                   fetcher.getFrameworks()
                                               ]);
        if (!releaseExt) return false;

        const details     = await releaseExt.getDetails();
        const workloadExt = await this.domain.workloadDefinitions.getExtendedById(details.workload_id);
        return workloadExt.areFrameworksCompatible(releaseExt.model.details.framework_ids);
    }

    public async getRun(assessmentRunId: string): Promise<Models.WellArchitectedFrameworkAssessmentRun>
    {
        const runs = await this.listAllRuns();
        return runs.find((run) => run.sysId == assessmentRunId);
    }

    public async getActiveRun(): Promise<Models.WellArchitectedFrameworkAssessmentRun>
    {
        let latestRun = await this.getLatestRun();

        if (latestRun?.status !== Models.WellArchitectedFrameworkAssessmentRunStatus.InProgress) latestRun = null;

        return latestRun;
    }

    public async getLatestRun(): Promise<Models.WellArchitectedFrameworkAssessmentRun>
    {
        const runs = await this.listAllRuns();
        if (!runs?.length) return null;

        return runs[runs.length - 1];
    }

    @Memoizer
    public async listAllRuns(): Promise<Models.WellArchitectedFrameworkAssessmentRun[]>
    {
        const runs = await this.domain.wafAssessmentRuns.listAllRuns(this.model.sysId);
        runs?.sort((runA,
                    runB) => MomentHelper.compareDates(runA.updatedOn, runB.updatedOn, true));
        return runs;
    }

    public async setActiveRunStatus(status: Models.WellArchitectedFrameworkAssessmentRunStatus): Promise<boolean>
    {
        const run = await this.getActiveRun();
        if (run && await this.domain.wafAssessmentRuns.setState(run.sysId, status))
        {
            this.resetRunCache();
            return true;
        }
        return false;
    }

    @ResetMemoizers
    public resetRunCache()
    {}
}

@Injectable()
export class WafAssessmentsListService extends BaseListService<Models.AssessmentDefinitionListRequest, Models.BatchRequest, Models.WellArchitectedFrameworkAssessmentDefinition>
{
    constructor(inj: Injector)
    {
        super(inj, Models.WellArchitectedFrameworkAssessmentDefinition);
    }

    @ReportError
    public list(listReq: Models.AssessmentDefinitionListRequest): Promise<Models.ListResponse>
    {
        return this.domain.apis.wafAssessmentDefinitionsApi.list(listReq);
    }

    @ReportError
    protected async getBatchResponseImpl(req: Models.BatchRequest): Promise<BatchResponse<Models.WellArchitectedFrameworkAssessmentDefinition>>
    {
        const recordIdBatchRequest = Models.RecordIdentityBatchRequest.newInstance({ids: req?.ids.map((id) => WorkloadDefinitionExtended.newIdentity(id))});
        const assessments          = await this.domain.apis.wafAssessmentDefinitionsApi.batch(recordIdBatchRequest);

        const lookup: Lookup<Models.WellArchitectedFrameworkAssessmentDefinition> = {};
        for (const assessment of assessments || []) lookup[assessment.sysId] = assessment;

        return {results: lookup};
    }

    protected createBatchRequest(listRequest: Models.AssessmentDefinitionListRequest,
                                 ids: string[]): Models.BatchRequest
    {
        return Models.BatchRequest.newInstance({ids: ids});
    }

    //--//

    public async listAllForTenant(tenantId: string): Promise<Models.RecordIdentity[]>
    {
        const assessmentFilter = Models.AssessmentFilter.newInstance({tenant_id: tenantId});
        const ids              = await super.listAll(Models.AssessmentDefinitionListRequest.newInstance({filters: assessmentFilter}));
        return ids.map(id => WafAssessmentDefinitionExtended.newIdentity(id));
    }
}
